import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uploadAvatar } from '../service/profileService'

/** Maximum file size allowed for avatar uploads (5 MB) */
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

/** MIME types accepted for avatar uploads */
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const

/** Result of an avatar upload attempt */
interface AvatarUploadResult {
  success: boolean
  avatarUrl?: string
  error?: string
}

/**
 * Converts a File to a base64 data URI string.
 * Required because the backend API expects base64 input for avatar uploads.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Hook for handling avatar image upload with client-side validation.
 * Validates file type and size before converting to base64 and sending to the API.
 */
export function useAvatarUpload() {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)

  /** Validates and uploads a file as the user's avatar */
  async function handleFileSelect(file: File): Promise<AvatarUploadResult> {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type as typeof ACCEPTED_IMAGE_TYPES[number])) {
      return { success: false, error: t('my_profile.avatar_invalid_type') }
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return { success: false, error: t('my_profile.avatar_too_large') }
    }

    setUploading(true)
    try {
      const base64 = await fileToBase64(file)
      const result = await uploadAvatar(base64)
      return { success: true, avatarUrl: result.avatarUrl }
    } catch (err) {
      console.error('[MyProfile] avatar upload failed:', err)
      return { success: false, error: t('my_profile.avatar_update_failed') }
    } finally {
      setUploading(false)
    }
  }

  /** Uploads a base64 image directly (e.g. from camera capture) */
  async function handleBase64Upload(base64: string): Promise<AvatarUploadResult> {
    setUploading(true)
    try {
      const result = await uploadAvatar(base64)
      return { success: true, avatarUrl: result.avatarUrl }
    } catch (err) {
      console.error('[MyProfile] avatar upload failed:', err)
      return { success: false, error: t('my_profile.avatar_update_failed') }
    } finally {
      setUploading(false)
    }
  }

  return { uploading, handleFileSelect, handleBase64Upload }
}
